"use client";

import React from "react";
import {
  useMultiSelectStore,
  getSelectionState,
} from "modules/common/stores/useMultiSelectStore";
import { Checkbox } from "modules/common/components/ui/Checkbox";
import { cn } from "utils/utils";

interface SelectionCheckboxProps {
  ids: string[];
  className?: string;
  /** Blocks selection while keeping the row visible (e.g. every copy is already in an active trade). */
  disabled?: boolean;
  /** Replaces the default aria-label when disabled, to explain why. */
  disabledLabel?: string;
}

export function SelectionCheckbox({
  ids,
  className,
  disabled = false,
  disabledLabel,
}: SelectionCheckboxProps) {
  const selectionState = useMultiSelectStore((s) =>
    getSelectionState(s.selectedIds, ids)
  );
  const selectAllActive = useMultiSelectStore((s) => s.selectAllActive);
  const toggleItem = useMultiSelectStore((s) => s.toggleItem);

  const checked = selectAllActive || selectionState === "all";
  const indeterminate = !selectAllActive && selectionState === "partial";

  // `disabled` blocks *new* selections only. Availability refreshes in the
  // background, so a row ticked while it had free copies can go fully
  // spoken-for mid-selection; blocking that one would strand it — displayed
  // unchecked, inert, but still in `selectedIds` and still sent.
  const blocked = disabled && !checked && !indeterminate;

  const handleChange = () => {
    if (blocked) return;
    toggleItem(ids);
  };

  return (
    <Checkbox
      checked={checked}
      indeterminate={indeterminate}
      onChange={handleChange}
      onClick={(e) => e.stopPropagation()}
      disabled={blocked}
      className={cn("rounded-full p-1 transition-none", className)}
      aria-label={
        blocked
          ? (disabledLabel ?? "Unavailable for selection")
          : checked
            ? "Deselect item"
            : "Select item"
      }
    />
  );
}
